import { useMemo } from 'react';
import { CalendarClock, CalendarDays, Stethoscope } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatValue } from '@/lib/format';
import { statusToneClass } from '@/lib/cabinet/appointments';
import {
    filterByRange,
    hasRange,
    isUpcoming,
    rangeInvalid,
    type AppointmentsHistory,
    type DateRange,
} from '@/components/medic/patientCardData';
import { DateRangeFilter } from '@/components/medic/PatientCardDateRange';
import { SectionState } from '@/components/medic/PatientCardStates';

interface Props {
    data: AppointmentsHistory;
    range: DateRange;
    onRangeChange: (next: DateRange) => void;
    onReload: () => void;
    onOpenAppointment: (appointmentId: string) => void;
}

function shortText(value: string, limit = 120): string {
    const text = value.trim();
    if (text.length <= limit) return text;
    return `${text.slice(0, limit)}…`;
}

export function PatientCardAppointmentsSection({
    data,
    range,
    onRangeChange,
    onReload,
    onOpenAppointment,
}: Props) {
    const items = useMemo(
        () => filterByRange(data.items, (item) => item.appointmentAt, range),
        [data.items, range],
    );
    const next = data.items.find((item) => isUpcoming(item.appointmentAt)) || null;

    if (data.state === 'denied') {
        return <SectionState title="Записи на приём недоступны" text={data.error || 'Нет прав на чтение приёмов.'} tone="warning" />;
    }
    if (data.state === 'unavailable') {
        return <SectionState title="Приёмы не ведутся" text={data.note || 'В этой базе нет записей на приём.'} />;
    }
    if (data.state === 'error') {
        return <SectionState title="Не удалось загрузить записи на приём" text={data.error} tone="danger" onRetry={onReload} />;
    }

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Всего записей</div>
                    <div className="text-lg font-semibold">{data.items.length}</div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Ближайшая</div>
                    <div className="truncate text-sm font-medium">
                        {next
                            ? formatValue(next.appointmentAt, '', 'timestamp')
                            : 'не запланирована'}
                    </div>
                </div>
                <div className="rounded-lg border bg-card px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">В периоде</div>
                    <div className="text-lg font-semibold">{items.length}</div>
                </div>
            </div>

            <DateRangeFilter value={range} onChange={onRangeChange} />

            {rangeInvalid(range) && (
                <SectionState
                    title="Период задан неверно"
                    text="Дата «с» позже даты «по». Исправьте период или сбросьте фильтр."
                    tone="warning"
                />
            )}

            {!rangeInvalid(range) && items.length === 0 && (
                <div className="rounded-lg border bg-card px-3 py-4 text-sm text-muted-foreground">
                    <div className="flex flex-wrap items-center gap-2">
                        <CalendarDays className="h-4 w-4 shrink-0" />
                        <span>
                            {data.items.length === 0
                                ? 'Записей на приём у пациента нет.'
                                : 'За выбранный период записей на приём нет.'}
                        </span>
                        {data.items.length > 0 && hasRange(range) && (
                            <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-[11px]"
                                onClick={() => onRangeChange({ from: '', to: '' })}
                            >
                                Сбросить период
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {!rangeInvalid(range) && items.length > 0 && (
                <div className="overflow-hidden rounded-lg border bg-card">
                    <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                        <CalendarClock className="h-3.5 w-3.5 text-primary" />
                        <span className="text-xs font-medium">Записи на приём</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">
                            показано {items.length} из {data.items.length}
                        </span>
                    </div>
                    {items.map((appointment) => {
                        const statusLabel = data.statusLabels[appointment.statusCode] || appointment.statusCode || 'Статус не указан';
                        const typeLabel = data.typeLabels[appointment.typeCode] || appointment.typeCode || '';
                        return (
                            <button
                                key={appointment.id}
                                type="button"
                                className="flex w-full items-start gap-3 border-b px-3 py-2.5 text-left last:border-0 hover:bg-accent/30"
                                onClick={() => onOpenAppointment(appointment.id)}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-medium">
                                            {appointment.appointmentAt
                                                ? formatValue(appointment.appointmentAt, 'date:dd.MM.yyyy HH:mm', 'timestamp')
                                                : 'Дата не указана'}
                                        </span>
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusToneClass(appointment.statusCode)}`}>
                                            {statusLabel}
                                        </span>
                                        {typeLabel && <span className="text-[10px] text-muted-foreground">{typeLabel}</span>}
                                    </div>
                                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                        <Stethoscope className="h-3 w-3" />
                                        <span>{appointment.doctorName || 'Врач не указан'}</span>
                                        {appointment.number && <span>· № {appointment.number}</span>}
                                    </div>
                                    {(appointment.complaints.trim() || appointment.conclusion.trim()) && (
                                        <div className="mt-1 text-xs text-foreground">
                                            {shortText(appointment.conclusion.trim() || appointment.complaints)}
                                        </div>
                                    )}
                                </div>
                                <span className="shrink-0 self-center text-[10px] text-muted-foreground">открыть</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
